import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AuthStatusResponse, Permission, PlatformFeatures, SessionUser } from '@ssn/shared';
import { api } from '@/lib/api';

interface SessionContextValue {
  readonly status: 'loading' | 'authenticated' | 'anonymous' | 'denied';
  readonly session: SessionUser | null;
  readonly denial: AuthStatusResponse['accessDenied'];
  readonly features: PlatformFeatures | null;
  /**
   * Permission check for the UI.
   *
   * This decides what is worth SHOWING. It is never what makes an action safe —
   * the server re-checks every request, and a hand-crafted call from a developer
   * without the permission is refused there (§21, §26).
   */
  can(permission: Permission): boolean;
  canAny(...permissions: Permission[]): boolean;
  refresh(): Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export const SESSION_QUERY_KEY = ['session'] as const;

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isPending } = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: ({ signal }) => api.get<AuthStatusResponse>('/api/auth/me', signal),
    // The session is the root of every permission decision on screen, so it is
    // re-checked whenever the user comes back to the tab: a role revoked while
    // they were away should not leave stale controls showing.
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: false,
  });

  const value = useMemo<SessionContextValue>(() => {
    const session = data?.session ?? null;
    const permissions = new Set(session?.permissions ?? []);

    const status: SessionContextValue['status'] = isPending
      ? 'loading'
      : data?.accessDenied
        ? 'denied'
        : session
          ? 'authenticated'
          : 'anonymous';

    return {
      status,
      session,
      denial: data?.accessDenied ?? null,
      features: session?.features ?? null,
      can: (permission) => permissions.has(permission),
      canAny: (...list) => list.some((permission) => permissions.has(permission)),
      refresh: async () => {
        await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
      },
    };
  }, [data, isPending, queryClient]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside a SessionProvider');
  return context;
}

/** The authenticated user, for screens that only render behind the auth gate. */
export function useCurrentUser() {
  const { session } = useSession();
  if (!session) throw new Error('useCurrentUser was called outside an authenticated route');
  return session.user;
}
